import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  DocumentArrowDownIcon,
  ChartBarIcon,
  CalendarIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  AdjustmentsHorizontalIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const FormSubmissions = () => {
  const { id } = useParams();
  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  useEffect(() => {
    // Simulate fetching form and submissions data
    const fetchData = () => {
      const mockForm = {
        id: parseInt(id),
        title: 'Patient Registration Form',
        version: '2.1',
        totalResponses: 145
      };

      const mockSubmissions = [
        {
          id: 'SUB001',
          submittedBy: {
            name: 'John Smith',
            email: 'john.smith@email.com',
            avatar: 'https://ui-avatars.com/api/?name=John+Smith&background=3b82f6&color=fff'
          },
          submittedDate: '2025-01-22T15:30:00Z',
          status: 'completed',
          completionTime: '8 minutes 32 seconds',
          ipAddress: '192.168.1.100',
          device: 'Desktop - Chrome',
          responses: {
            'Full Name': 'John Smith',
            'Email Address': 'john.smith@email.com',
            'Phone Number': '(555) 123-4567',
            'Date of Birth': '1985-03-15',
            'Gender': 'Male',
            'Medical History': 'No significant medical history',
            'Insurance Provider': 'Blue Cross Blue Shield',
            'Policy Number': 'BC123456789',
            'Emergency Contact Name': 'Jane Smith',
            'Emergency Contact Phone': '(555) 987-6543'
          }
        },
        {
          id: 'SUB002',
          submittedBy: {
            name: 'Mary Johnson',
            email: 'mary.johnson@email.com',
            avatar: 'https://ui-avatars.com/api/?name=Mary+Johnson&background=10b981&color=fff'
          },
          submittedDate: '2025-01-22T14:15:00Z',
          status: 'completed',
          completionTime: '12 minutes 8 seconds',
          ipAddress: '192.168.1.101',
          device: 'Mobile - Safari',
          responses: {
            'Full Name': 'Mary Johnson',
            'Email Address': 'mary.johnson@email.com',
            'Phone Number': '(555) 234-5678',
            'Date of Birth': '1992-07-22',
            'Gender': 'Female',
            'Medical History': 'Hypertension, managed with medication',
            'Insurance Provider': 'Aetna',
            'Policy Number': 'AET987654321',
            'Emergency Contact Name': 'Robert Johnson',
            'Emergency Contact Phone': '(555) 876-5432'
          }
        },
        {
          id: 'SUB003',
          submittedBy: {
            name: 'Robert Brown',
            email: 'robert.brown@email.com',
            avatar: 'https://ui-avatars.com/api/?name=Robert+Brown&background=f59e0b&color=fff'
          },
          submittedDate: '2025-01-22T13:45:00Z',
          status: 'in_progress',
          completionTime: null,
          ipAddress: '192.168.1.102',
          device: 'Desktop - Firefox',
          responses: {
            'Full Name': 'Robert Brown',
            'Email Address': 'robert.brown@email.com',
            'Phone Number': '(555) 345-6789',
            'Date of Birth': '1978-11-03',
            'Gender': 'Male',
            'Medical History': '',
            'Insurance Provider': '',
            'Policy Number': '',
            'Emergency Contact Name': '',
            'Emergency Contact Phone': ''
          }
        },
        {
          id: 'SUB004',
          submittedBy: {
            name: 'Sarah Davis',
            email: 'sarah.davis@email.com',
            avatar: 'https://ui-avatars.com/api/?name=Sarah+Davis&background=ef4444&color=fff'
          },
          submittedDate: '2025-01-22T12:20:00Z',
          status: 'completed',
          completionTime: '6 minutes 45 seconds',
          ipAddress: '192.168.1.103',
          device: 'Tablet - Chrome',
          responses: {
            'Full Name': 'Sarah Davis',
            'Email Address': 'sarah.davis@email.com',
            'Phone Number': '(555) 456-7890',
            'Date of Birth': '1989-05-18',
            'Gender': 'Female',
            'Medical History': 'Diabetes Type 2, diet controlled',
            'Insurance Provider': 'United Healthcare',
            'Policy Number': 'UH456789123',
            'Emergency Contact Name': 'Michael Davis',
            'Emergency Contact Phone': '(555) 765-4321'
          }
        },
        {
          id: 'SUB005',
          submittedBy: {
            name: 'Michael Wilson',
            email: 'michael.wilson@email.com',
            avatar: 'https://ui-avatars.com/api/?name=Michael+Wilson&background=8b5cf6&color=fff'
          },
          submittedDate: '2025-01-22T11:10:00Z',
          status: 'abandoned',
          completionTime: null,
          ipAddress: '192.168.1.104',
          device: 'Mobile - Chrome',
          responses: {
            'Full Name': 'Michael Wilson',
            'Email Address': 'michael.wilson@email.com',
            'Phone Number': '',
            'Date of Birth': '',
            'Gender': '',
            'Medical History': '',
            'Insurance Provider': '',
            'Policy Number': '',
            'Emergency Contact Name': '',
            'Emergency Contact Phone': ''
          }
        }
      ];

      setForm(mockForm);
      setSubmissions(mockSubmissions);
      setFilteredSubmissions(mockSubmissions);
      setLoading(false);
    };

    setTimeout(fetchData, 500);
  }, [id]);

  useEffect(() => {
    // Filter and sort submissions
    let filtered = submissions.filter(submission => {
      const matchesSearch = submission.submittedBy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           submission.submittedBy.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           submission.id.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || submission.status === statusFilter;
      
      let matchesDate = true;
      if (dateFilter !== 'all') {
        const submissionDate = new Date(submission.submittedDate);
        const now = new Date();
        
        switch (dateFilter) {
          case 'today':
            matchesDate = submissionDate.toDateString() === now.toDateString();
            break;
          case 'week':
            const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            matchesDate = submissionDate >= weekAgo;
            break;
          case 'month':
            const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            matchesDate = submissionDate >= monthAgo;
            break;
          default:
            matchesDate = true;
        }
      }
      
      return matchesSearch && matchesStatus && matchesDate;
    });

    // Sort submissions
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.submittedBy.name.localeCompare(b.submittedBy.name);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'completionTime':
          const aTime = a.completionTime ? parseFloat(a.completionTime.split(' ')[0]) : 0;
          const bTime = b.completionTime ? parseFloat(b.completionTime.split(' ')[0]) : 0;
          comparison = aTime - bTime;
          break;
        default: // date
          comparison = new Date(a.submittedDate) - new Date(b.submittedDate);
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredSubmissions(filtered);
  }, [submissions, searchTerm, statusFilter, dateFilter, sortBy, sortOrder]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'abandoned':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress':
        return 'In Progress';
      case 'abandoned':
        return 'Abandoned';
      default:
        return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'in_progress':
        return <ClockIcon className="h-4 w-4" />;
      case 'abandoned':
        return <ExclamationTriangleIcon className="h-4 w-4" />;
      default:
        return <InformationCircleIcon className="h-4 w-4" />;
    }
  };

  const handleSelectSubmission = (submissionId) => {
    setSelectedSubmissions(prev => 
      prev.includes(submissionId)
        ? prev.filter(id => id !== submissionId)
        : [...prev, submissionId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSubmissions.length === filteredSubmissions.length) {
      setSelectedSubmissions([]);
    } else {
      setSelectedSubmissions(filteredSubmissions.map(sub => sub.id));
    }
  };

  const exportSubmissions = () => {
    // Simulate export functionality
    alert('Export functionality would be implemented here');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Link
                  to={`/forms/${id}`}
                  className="mr-4 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Back to Form
                </Link>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Form Submissions</h1>
                  <p className="text-sm text-gray-500">{form?.title}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Link
                  to={`/forms/${id}/analytics`}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <ChartBarIcon className="h-4 w-4 mr-2" />
                  Analytics
                </Link>
                
                <button
                  onClick={exportSubmissions}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  Export
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Submissions</dt>
                  <dd className="text-lg font-medium text-gray-900">{submissions.length}</dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Completed</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {submissions.filter(sub => sub.status === 'completed').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">In Progress</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {submissions.filter(sub => sub.status === 'in_progress').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Abandoned</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {submissions.filter(sub => sub.status === 'abandoned').length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="flex-1 max-w-lg">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search submissions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center space-x-4">
              {/* Filters Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                Filters
              </button>

              {/* Sort */}
              <div className="relative">
                <select
                  value={`${sortBy}-${sortOrder}`}
                  onChange={(e) => {
                    const [field, order] = e.target.value.split('-');
                    setSortBy(field);
                    setSortOrder(order);
                  }}
                  className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="name-asc">Name A-Z</option>
                  <option value="name-desc">Name Z-A</option>
                  <option value="status-asc">Status</option>
                  <option value="completionTime-asc">Fastest First</option>
                  <option value="completionTime-desc">Slowest First</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <ChevronDownIcon className="h-4 w-4" />
                </div>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Status</option>
                    <option value="completed">Completed</option>
                    <option value="in_progress">In Progress</option>
                    <option value="abandoned">Abandoned</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date Range
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">Last 7 Days</option>
                    <option value="month">Last 30 Days</option>
                  </select>
                </div>

                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setStatusFilter('all');
                      setDateFilter('all');
                      setSearchTerm('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Submissions Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedSubmissions.length === filteredSubmissions.length && filteredSubmissions.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  {selectedSubmissions.length > 0 
                    ? `${selectedSubmissions.length} selected`
                    : `${filteredSubmissions.length} submissions`
                  }
                </span>
              </div>
              
              {selectedSubmissions.length > 0 && (
                <div className="flex items-center space-x-2">
                  <button className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
                    Export Selected
                  </button>
                  <button className="px-3 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50">
                    Delete Selected
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Table Content */}
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No submissions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredSubmissions.map((submission) => (
                <div key={submission.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedSubmissions.includes(submission.id)}
                        onChange={() => handleSelectSubmission(submission.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-4 flex items-center">
                        <img
                          className="h-10 w-10 rounded-full"
                          src={submission.submittedBy.avatar}
                          alt={submission.submittedBy.name}
                        />
                        <div className="ml-4">
                          <div className="flex items-center">
                            <h3 className="text-sm font-medium text-gray-900">
                              {submission.submittedBy.name}
                            </h3>
                            <span className="ml-2 text-sm text-gray-500">#{submission.id}</span>
                          </div>
                          <p className="text-sm text-gray-500">{submission.submittedBy.email}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-6 text-sm text-gray-500">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                          {getStatusIcon(submission.status)}
                          <span className="ml-1">{getStatusLabel(submission.status)}</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {formatDate(submission.submittedDate)}
                      </div>
                      
                      {submission.completionTime && (
                        <div className="flex items-center">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {submission.completionTime}
                        </div>
                      )}
                      
                      <Link
                        to={`/forms/${id}/submissions/${submission.id}`}
                        className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <EyeIcon className="h-3 w-3 mr-1" />
                        View
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormSubmissions;
